import { useContext, useRef, useMemo } from 'react';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { useLocation } from '@thd-olt-component-react/router';
import { parseUrl, getNavParam, PICK_UP_TODAY_REFINEMENT_KEY } from '../product-results-helpers';
import useAdditionalSearchParams from './useAdditionalSearchParams';

const CHECK_CAT_STYLE = /catStyle=ShowProducts/i;

const useProductResultsParamsPLP = (props) => {
  const {
    pageType
  } = props;

  const isPEP = pageType === 'pep';
  const isSearch = pageType === 'search';
  const isBrowse = pageType === 'browse';
  const disableSSR = typeof window !== 'undefined' && window.DISABLE_SSR;
  const varnish = !disableSSR && !isSearch;
  const store = useStore({ varnish, online: false });
  const ctx = useContext(ExperienceContext);
  const { pathname, search } = useLocation();
  const path = `${pathname}${search}`;
  const { deliveryZip: serverDeliveryZip, clientStore, nearByStores = [], isConsumerApp } = ctx;
  const { isCustomerIdentified = false } = useThdCustomer() || {};

  const SERVER_SIDE_PRODUCT_POD_COUNT = isSearch ? 24 : 12;
  const CLIENT_SIDE_PRODUCT_POD_COUNT = 24;

  const urlParams = useMemo(() => {
    const cleanPath = path.replace(/^\/render[^/]*/, '');
    return parseUrl({ path: cleanPath });
  }, [path]);

  const {
    keyword,
    lowerbound,
    Nao = 0,
    nValue,
    sortby,
    sortorder,
    upperbound,
    skipSpellCheck
  } = urlParams;
  const searchNavPropsRef = useRef({
    askForSponsored: isSearch,
    deliveryZip: serverDeliveryZip,
    pageSize: disableSSR ? CLIENT_SIDE_PRODUCT_POD_COUNT : SERVER_SIDE_PRODUCT_POD_COUNT
  });

  const totalPageSizeRef = useRef(disableSSR ? CLIENT_SIDE_PRODUCT_POD_COUNT : SERVER_SIDE_PRODUCT_POD_COUNT);
  const hasSponsoredSorts = !sortby || ['bestmatch', 'topsellers'].indexOf(sortby) > -1;
  const showProducts = CHECK_CAT_STYLE.test(path);

  const navParam = getNavParam({
    nValue
  });
  const storefilter = 'ALL';
  let storeFilterOpts = (navParam || '').includes(PICK_UP_TODAY_REFINEMENT_KEY) ? 'IN_STORE' : storefilter;
  let multiStoreIds = [...nearByStores];
  const hasCurrentStore = !!(multiStoreIds.find((storeIdItem) => storeIdItem === store?.storeId));
  if (multiStoreIds.length && !hasCurrentStore) {
    multiStoreIds.unshift(store?.storeId);
  }
  multiStoreIds = (!multiStoreIds.length && storeFilterOpts === 'IN_STORE' && isConsumerApp)
    ? [store?.storeId] : multiStoreIds;

  let THDCustomer = {};
  if (clientStore) {
    let deliveryZipCode;
    if (
      typeof window !== 'undefined'
      && window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails
    ) {
      deliveryZipCode = window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails();
    }
    searchNavPropsRef.current = {
      pageSize: CLIENT_SIDE_PRODUCT_POD_COUNT,
      askForSponsored: true,
      deliveryZip: deliveryZipCode?.zipcode
    };
    totalPageSizeRef.current = CLIENT_SIDE_PRODUCT_POD_COUNT;

    THDCustomer = typeof window !== 'undefined' ? window.THDCustomer?.default : {};
  }
  const isClientSide = typeof window !== 'undefined';
  const shouldSkipUntilLocalized = searchNavPropsRef.current?.pageSize === 12 && !store?.isLocalized && isClientSide;

  const additionalSearchParams = useAdditionalSearchParams({
    askForSponsored: searchNavPropsRef.current?.askForSponsored,
    hasSponsoredSorts,
    deliveryZip: searchNavPropsRef.current?.deliveryZip,
    showProducts,
    skipSpellCheck,
    isPEP,
    shouldSkipUntilLocalized,
    isStoreLocalized: store?.isLocalized,
  });

  // Wrap return value in useMemo to react to context/store changes
  const productResultsParams = useMemo(() => ({
    additionalSearchParams: {
      ...additionalSearchParams,
      multiStoreIds
    },
    keyword,
    lowerbound,
    nValue,
    start: Nao,
    storeId: store?.storeId,
    isLocalized: store?.isLocalized,
    sortby,
    sortorder,
    upperbound,
    isCustomerIdentified: store?.isLocalized ? isCustomerIdentified : false,
    pageSize: searchNavPropsRef.current?.pageSize,
    path,
    isShowProducts: showProducts,
  }), [
    additionalSearchParams,
    nearByStores,
    storeFilterOpts,
    isConsumerApp,
    keyword,
    lowerbound,
    nValue,
    Nao,
    store?.storeId,
    store?.isLocalized,
    sortby,
    sortorder,
    upperbound,
    isCustomerIdentified,
    path,
  ]);
  return productResultsParams;
};

export default useProductResultsParamsPLP;